import { Typography } from "@mui/material";
import {
  LineChart,
  Line,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Label,
  ResponsiveContainer,
} from "recharts";
import DuomenuNera from "../shared/DuomenuNera";
import useBreakpoints from "../../styles/windowSizes";

const GrafikaiSuRibomis = ({
  duomenys,
  rodiklis,
  apatinisRezis,
  virsutinisRezis,
  raktas,
  id,
  pavadinimas,
  height = 240,
  minRiba = null,
  etiketeIki = null,
}) => {
  const { isMd, isLg, isXl } = useBreakpoints();

  // Add dummy data point at the beginning for spacing
  const paddedDuomenys =
    duomenys.length > 0
      ? [
          {
            [raktas]: "", // Empty string for X-axis
            [rodiklis]: null, // Null value so it doesn't render
          },
          ...duomenys,
        ]
      : duomenys;

  const rodiklioReiksmes = duomenys
    .map((item) => item[rodiklis])
    .filter((val) => val !== null && val !== undefined && val !== 0 && !isNaN(val));

  if (rodiklioReiksmes.length === 0) {
    return <DuomenuNera pavadinimas={pavadinimas} />;
  }

  const minRodiklioReiksme = Math.min(...rodiklioReiksmes);
  const maxRodiklioReiksme = Math.max(...rodiklioReiksmes);

  let breakPoint1 = 0;
  let breakPoint2 = 100;

  if (apatinisRezis !== null && virsutinisRezis !== null) {
    // Both boundaries exist
    breakPoint1 =
      ((maxRodiklioReiksme - virsutinisRezis) / (maxRodiklioReiksme - minRodiklioReiksme)) * 100;
    breakPoint2 =
      ((maxRodiklioReiksme - apatinisRezis) / (maxRodiklioReiksme - minRodiklioReiksme)) * 100;
  } else if (apatinisRezis !== null && virsutinisRezis === null) {
    // Only lower boundary exists
    if (minRodiklioReiksme >= apatinisRezis) {
      // All values are above the threshold - entire line should be green
      breakPoint1 = 0;
      breakPoint2 = 0; // Set to 0 so entire gradient is green
    } else {
      // Some values are below threshold
      breakPoint2 =
        ((maxRodiklioReiksme - apatinisRezis) / (maxRodiklioReiksme - minRodiklioReiksme)) * 100;
      breakPoint1 = 0;
    }
  } else if (apatinisRezis === null && virsutinisRezis !== null) {
    // Only upper boundary exists
    if (maxRodiklioReiksme <= virsutinisRezis) {
      // All values are below the threshold - entire line should be green
      breakPoint1 = 100;
      breakPoint2 = 100; // Set to 100 so entire gradient is green
    } else {
      // Some values are above threshold
      breakPoint1 =
        ((maxRodiklioReiksme - virsutinisRezis) / (maxRodiklioReiksme - minRodiklioReiksme)) * 100;
      breakPoint2 = 100;
    }
  }

  const validBreakPoint1 = Math.max(0, Math.min(100, breakPoint1));
  const validBreakPoint2 = Math.max(0, Math.min(100, breakPoint2));

  const CustomDot = (props) => {
    const { cx, cy, payload } = props;

    if (
      !payload ||
      payload[rodiklis] === null ||
      payload[rodiklis] === undefined ||
      payload[rodiklis] === 0
    ) {
      return null;
    }

    let color = "green"; // Default to green when no boundaries

    if (apatinisRezis !== null && virsutinisRezis !== null) {
      color = "red"; // Default for outside range
      if (payload[rodiklis] >= apatinisRezis && payload[rodiklis] <= virsutinisRezis) {
        color = "green";
      }
    } else if (apatinisRezis !== null && virsutinisRezis === null) {
      color = payload[rodiklis] >= apatinisRezis ? "green" : "red";
    } else if (apatinisRezis === null && virsutinisRezis !== null) {
      color = payload[rodiklis] <= virsutinisRezis ? "green" : "red";
    }

    return (
      <g>
        <circle cx={cx} cy={cy} r={4} fill={color} stroke={color} strokeWidth={1} />
        {((etiketeIki === "isXl" && isXl) ||
          (etiketeIki === "isLg" && (isXl || isLg)) ||
          (etiketeIki === "isMd" && (isXl || isLg || isMd)) ||
          (!etiketeIki && (isXl || isLg || isMd))) && (
          <text
            x={cx}
            y={cy - 10}
            textAnchor="middle"
            fontSize={17}
            fill={color}
            style={{ zIndex: 1000, position: "absolute", overflow: "visible" }}
          >
            {payload[rodiklis]}
          </text>
        )}
      </g>
    );
  };

  const createGradient = () => {
    if (apatinisRezis !== null && virsutinisRezis !== null) {
      return (
        <linearGradient id={id} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="red" />
          <stop offset={`${validBreakPoint1}%`} stopColor="red" />
          <stop offset={`${validBreakPoint1}%`} stopColor="green" />
          <stop offset={`${validBreakPoint2}%`} stopColor="green" />
          <stop offset={`${validBreakPoint2}%`} stopColor="red" />
          <stop offset="100%" stopColor="red" />
        </linearGradient>
      );
    } else if (apatinisRezis !== null && virsutinisRezis === null) {
      // Only lower boundary - check if all values are above threshold
      if (minRodiklioReiksme >= apatinisRezis) {
        // All values are good - entire line green
        return (
          <linearGradient id={id} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="green" />
            <stop offset="100%" stopColor="green" />
          </linearGradient>
        );
      } else {
        // Some values below threshold
        return (
          <linearGradient id={id} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="green" />
            <stop offset={`${validBreakPoint2}%`} stopColor="green" />
            <stop offset={`${validBreakPoint2}%`} stopColor="red" />
            <stop offset="100%" stopColor="red" />
          </linearGradient>
        );
      }
    } else if (apatinisRezis === null && virsutinisRezis !== null) {
      // Only upper boundary - check if all values are below threshold
      if (maxRodiklioReiksme <= virsutinisRezis) {
        // All values are good - entire line green
        return (
          <linearGradient id={id} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="green" />
            <stop offset="100%" stopColor="green" />
          </linearGradient>
        );
      } else {
        // Some values above threshold
        return (
          <linearGradient id={id} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="red" />
            <stop offset={`${validBreakPoint1}%`} stopColor="red" />
            <stop offset={`${validBreakPoint1}%`} stopColor="green" />
            <stop offset="100%" stopColor="green" />
          </linearGradient>
        );
      }
    } else {
      // No boundaries - entire line green
      return (
        <linearGradient id={id} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="green" />
          <stop offset="100%" stopColor="green" />
        </linearGradient>
      );
    }
  };

  return (
    <>
      <Typography>{pavadinimas}</Typography>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={paddedDuomenys}
          margin={{ top: 25, right: 30, left: 5 }} // Add margins to prevent clipping
        >
          <defs>{createGradient()}</defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis axisLine={true} tickMargin={10} dataKey={raktas} />
          <YAxis
            tickMargin={10} // Add space between tick and axis
            domain={[
              minRiba !== null
                ? minRiba
                : Number((minRodiklioReiksme - minRodiklioReiksme / 10).toFixed(0)),
              Number((maxRodiklioReiksme + maxRodiklioReiksme / 8).toFixed(0)),
            ]}
          />

          {apatinisRezis !== null && (
            <ReferenceLine y={apatinisRezis} stroke="#F4631E" strokeDasharray="7 3" strokeWidth={2}>
              <Label
                value={apatinisRezis}
                strokeOpacity={0.6}
                position="insideBottomRight"
                stroke="#F4631E"
              />
            </ReferenceLine>
          )}

          {virsutinisRezis !== null && (
            <ReferenceLine
              y={virsutinisRezis}
              stroke="#F4631E"
              strokeDasharray="7 3"
              strokeWidth={2}
            >
              <Label
                value={virsutinisRezis}
                strokeOpacity={0.6}
                position="insideTopLeft"
                stroke="#F4631E"
              />
            </ReferenceLine>
          )}

          <Line
            type="monotone"
            dataKey={rodiklis}
            stroke={apatinisRezis === null && virsutinisRezis === null ? "green" : `url(#${id})`}
            strokeWidth={2}
            dot={<CustomDot />}
            activeDot={false}
            connectNulls={false}
          />
          <Tooltip />
        </LineChart>
      </ResponsiveContainer>
    </>
  );
};

export default GrafikaiSuRibomis;
